import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SimpleMenuView from '@/components/menu-view/core/SimpleMenuView';
import LoadingLogo from '@/components/LoadingLogo';
import MenuError from '@/components/menu-view/core/MenuError';
import MenuWatermark from '@/components/menu-view/features/MenuWatermark';
import { useMenuData } from '@/hooks/useMenuData';
import { supabase } from '@/integrations/supabase/client';
import ReservationButton from '@/components/menu-view/features/ReservationButton';
import { trackMenuView, trackQrScan } from '@/services/analyticsService';
import { SEOHead } from '@/components/SEOHead';
import { Hotel, ArrowLeft, MapPin } from 'lucide-react';
import ZoneDetailDialog, { type ZoneDetail } from '@/components/zones/ZoneDetailDialog';

const MenuView = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { menu, items, loading, error } = useMenuData(slug);
  const [isMenuActive, setIsMenuActive] = useState(true);
  const [restaurantData, setRestaurantData] = useState<any>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [showWatermark, setShowWatermark] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [hotelLinktreeUrl, setHotelLinktreeUrl] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneDetail[]>([]);
  const [showZonesPanel, setShowZonesPanel] = useState(false);
  const [selectedZone, setSelectedZone] = useState<ZoneDetail | null>(null);

  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!menu?.restaurant_id) {
        setSubscriptionChecked(true);
        return;
      }
      
      try {
        // Fetch restaurant data for SEO
        const { data: restData } = await supabase
          .from('restaurants')
          .select('user_id, name, phone, address, type')
          .eq('id', menu.restaurant_id)
          .maybeSingle();
        
        if (restData) {
          setRestaurantData(restData);
          // If hotel/camping type, check for linktree
          const LINKTREE_TYPES: Record<string, string> = {
            hotel: 'hotel',
            camping: 'camping',
            club_vacances: 'camping',
            glamping: 'camping',
          };
          const linktreePrefix = LINKTREE_TYPES[restData.type];
          if (linktreePrefix) {
            const [linktreeRes, zonesRes] = await Promise.all([
              supabase.from('hotel_linktree_settings').select('id').eq('restaurant_id', menu.restaurant_id).maybeSingle(),
              supabase.from('hotel_zones').select('id, name, zone_type, description, opening_hours, photo_url, practical_info').eq('restaurant_id', menu.restaurant_id).eq('is_active', true),
            ]);
            if (linktreeRes.data) {
              setHotelLinktreeUrl(`/${linktreePrefix}/${menu.restaurant_id}`);
            }
            if (zonesRes.data && zonesRes.data.length > 0) {
              setZones(zonesRes.data as ZoneDetail[]);
              // Auto-open zone detail popup if ?zone= param matches a zone name
              const params = new URLSearchParams(window.location.search);
              const zoneParam = params.get('zone');
              if (zoneParam) {
                const matchedZone = zonesRes.data.find(
                  (z: any) => z.name.toLowerCase() === decodeURIComponent(zoneParam).toLowerCase() || z.id === zoneParam
                );
                if (matchedZone) {
                  setSelectedZone(matchedZone as ZoneDetail);
                }
              }
            }
          }
        }

        // Check if current user is owner
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id && restData?.user_id === session.user.id) {
          setIsOwner(true);
        }

        // Check subscription for the RESTAURANT (not the logged-in user)
        // Use is_admin() SECURITY DEFINER function to check if owner is founder
        let isFounder = false;
        if (restData?.user_id) {
          const { data: adminCheck } = await supabase.rpc('is_admin', { _user_id: restData.user_id });
          isFounder = !!adminCheck;
        }

        if (isFounder) {
          setShowWatermark(false);
        } else {
          try {
            const { data: subData } = await supabase.functions.invoke('check-subscription', {
              body: { restaurantId: menu.restaurant_id }
            });
            const isPaid = subData?.subscribed === true || subData?.subscription_status === 'active' || subData?.subscription_status === 'trialing';
            setShowWatermark(!isPaid);
          } catch {
            setShowWatermark(true);
          }
        }

        setIsMenuActive(true);
      } catch (err) {
        setIsMenuActive(true);
        setShowWatermark(false);
      }
      setSubscriptionChecked(true);
    };
    
    if (menu) {
      if (menu.restaurant_id && menu.id) {
        trackMenuView(menu.restaurant_id, menu.id).catch(() => {});
        
        // Track QR scan if room or zone param is present
        const params = new URLSearchParams(window.location.search);
        const roomNumber = params.get('room');
        const zoneId = params.get('zone');
        if (roomNumber || zoneId) {
          trackQrScan(menu.restaurant_id, menu.id, {
            roomNumber: roomNumber || undefined,
            zoneId: zoneId || undefined,
            source: 'qr',
          }).catch(() => {});
        }
      }
      checkSubscriptionStatus();
    }
  }, [menu]);

  const handleActivateMenu = () => {
    navigate(`/menu-preview?menu=${slug}`);
  };

  if (loading || !subscriptionChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingLogo />
      </div>
    );
  }

  if (error || !menu) {
    return <MenuError message={error || 'Menu not found'} />;
  }
  
  const menuType: 'restaurant' | 'website' = menu.type === "website" ? "website" : "restaurant";

  const restaurantName = restaurantData?.name || menu.name || "Restaurant";
  const baseUrl = "https://miam.food";
  const menuUrl = `${baseUrl}/menu/${slug}`;
  const bannerUrl = menu.banner_url || `${baseUrl}/og-image.png`;
  
  const seoTitle = `${restaurantName} - Menu en ligne | Voir la carte`;
  const seoDescription = menu.description 
    ? `Découvrez le menu de ${restaurantName}. ${menu.description}. Consultez notre carte en ligne et faites votre choix.`
    : `Découvrez le menu de ${restaurantName}. Consultez notre carte en ligne avec tous nos plats, boissons et tarifs.`;
  
  const seoKeywords = `menu ${restaurantName.toLowerCase()}, carte ${restaurantName.toLowerCase()}, restaurant ${restaurantData?.address || ''}, ${menuType}, ${restaurantData?.type || 'restaurant'}, menu en ligne, carte digitale`;
  
  // Check if we're in preview mode (embedded in an iframe on preview pages)
  const isPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
  
  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={menuUrl}
        ogImage={bannerUrl}
        restaurantName={restaurantName}
        restaurantAddress={restaurantData?.address}
        restaurantPhone={restaurantData?.phone || menu.phone}
        menuType={menuType}
        cuisine={restaurantData?.type}
      />
      
      {/* Watermark pour compte non payé - visible par tous sauf en mode preview */}
      {showWatermark && !isPreview && (
        <MenuWatermark onActivate={isOwner ? handleActivateMenu : undefined} />
      )}
      
      <SimpleMenuView menu={menu} menuItems={items} />
      
      {/* Back to hotel linktree button */}
      {hotelLinktreeUrl && (
        <a
          href={hotelLinktreeUrl}
          className="fixed top-3 left-3 z-50 flex items-center gap-2 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-2 rounded-full shadow-lg hover:bg-white transition-all text-sm font-medium border border-gray-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <Hotel className="w-4 h-4" />
          <span className="hidden sm:inline">Tous les services</span>
        </a>
      )}

      {/* Nos espaces button */}
      {zones.length > 0 && (
        <button
          onClick={() => setShowZonesPanel(!showZonesPanel)}
          className="fixed top-3 right-3 z-50 flex items-center gap-2 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-2 rounded-full shadow-lg hover:bg-white transition-all text-sm font-medium border border-gray-200"
        >
          <MapPin className="w-4 h-4" />
          <span className="hidden sm:inline">Nos espaces</span>
        </button>
      )}

      {/* Zones dropdown panel */}
      {showZonesPanel && zones.length > 0 && (
        <div className="fixed top-14 right-3 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 w-72 max-h-80 overflow-y-auto">
          <div className="p-3 border-b border-gray-100">
            <h3 className="font-semibold text-sm text-gray-900">📍 Nos espaces</h3>
          </div>
          <div className="p-2">
            {zones.map(zone => (
              <button
                key={zone.id}
                onClick={() => { setSelectedZone(zone); setShowZonesPanel(false); }}
                className="w-full text-left flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                {zone.photo_url ? (
                  <img src={zone.photo_url} alt={zone.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{zone.name}</p>
                  {zone.opening_hours && <p className="text-xs text-gray-500 truncate">{zone.opening_hours}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <ZoneDetailDialog
        zone={selectedZone}
        isOpen={!!selectedZone}
        onClose={() => setSelectedZone(null)}
      />
      
      {menu.restaurant_id && (
        <ReservationButton 
          restaurantId={menu.restaurant_id} 
          menuType={menuType}
          primaryColor={
            typeof menu.custom_css === 'object' && menu.custom_css !== null 
              ? (menu.custom_css as any).primaryColor || menu.primary_color 
              : menu.primary_color
          }
          restaurantName={menu.name || "Restaurant"}
        />
      )}
    </>
  );
};

export default MenuView;
